// components/Modals/ShareProfileModal.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Typography,
  Slide,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Copy as CopyIcon,
  Twitter,
  Linkedin,
  Facebook,
  MessageCircle as WhatsApp,
  Share2 as NativeShareIcon,
} from "lucide-react";
import { useState, useEffect, forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { useSnackbar } from "notistack";
import { getCurrentPageUrl } from "../../utils/getCurrentPageUrl";

interface ShareProfileModalProps {
  open: boolean;
  onClose: () => void;
}

/* Slide-up transition */
const Transition = forwardRef(function Transition(props: any, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ShareProfileModal = ({ open, onClose }: ShareProfileModalProps) => {
  const [url, setUrl] = useState("");
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const shareText = "Check out my profile on StageLink!";

  /* Fetch live URL and auto-select it when modal opens */
  useEffect(() => {
    if (open) {
      setUrl(getCurrentPageUrl());
      setTimeout(() => {
        (document.getElementById("shareable-link-input") as HTMLInputElement)
          ?.select();
      }, 150);
    }
  }, [open]);

  /* Copy to clipboard (+ snackbar) */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      enqueueSnackbar("Link copied to clipboard!", { variant: "success" });
      onClose();
    } catch {
      enqueueSnackbar("Failed to copy link", { variant: "error" });
    }
  };

  /* Helper to open a centered share popup */
  const openWindow = (shareUrl: string) =>
    window.open(
      shareUrl,
      "_blank",
      "noopener,noreferrer,width=600,height=600"
    );

  /* Social share handlers */
  const handleShareX = () =>
    openWindow(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        url
      )}&text=${encodeURIComponent(shareText)}`
    );

  const handleShareLinkedIn = () =>
    openWindow(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        url
      )}`
    );

  const handleShareFacebook = () =>
    openWindow(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    );

  const handleShareWhatsApp = () =>
    openWindow(
      `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`
    );

  /* Native Web-Share (mobile) with fallback to copy */
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: shareText, url });
        onClose();
        return;
      } catch {
        /* fall through */
      }
    }
    handleCopy();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          px: isMobile ? 2 : 3,
          py: 2.5,
          backgroundColor: "#fff",
          fontFamily: "'Lexend', sans-serif",
        },
      }}
    >
      <DialogTitle variant={isMobile ? "h6" : "h5"} fontWeight={700}>
        Share Your Profile
      </DialogTitle>

      <DialogContent sx={{ pt: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ color: "var(--neutral-600)", mb: 1.5 }}
        >
          Copy the link, scan the QR code, or share directly:
        </Typography>

        {/* 1️⃣ Share link field */}
        <TextField
          id="shareable-link-input"
          fullWidth
          value={url}
          label="Shareable Link"
          variant="outlined"
          InputProps={{
            readOnly: true,
            endAdornment: (
              <Tooltip title="Copy link">
                <IconButton
                  onClick={handleCopy}
                  sx={{ color: "primary.main" }}
                  size={isMobile ? "small" : "medium"}
                >
                  <CopyIcon size={18} />
                </IconButton>
              </Tooltip>
            ),
          }}
          sx={{
            mb: 3,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
            fontSize: isMobile ? "0.8rem" : "0.9rem",
          }}
        />

        {/* 2️⃣ QR code */}
        <Stack alignItems="center" mb={2}>
          <QRCodeCanvas
            value={url || "https://taasnet.com"}
            size={isMobile ? 120 : 160}
            fgColor="#000"
            style={{ borderRadius: 8 }}
          />
        </Stack>

        {/* 3️⃣ Social icon row (wrap on narrow screens) */}
        <Stack
          direction="row"
          spacing={1.5}
          flexWrap="wrap"
          justifyContent="center"
          mb={isMobile ? 1 : 0}
        >
          <Tooltip title="Share on X / Twitter">
            <IconButton onClick={handleShareX} size="small">
              <Twitter size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share on LinkedIn">
            <IconButton onClick={handleShareLinkedIn} size="small">
              <Linkedin size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share on Facebook">
            <IconButton onClick={handleShareFacebook} size="small">
              <Facebook size={18} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Share on WhatsApp">
            <IconButton onClick={handleShareWhatsApp} size="small">
              <WhatsApp size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogContent>

      {/* 4️⃣ Action buttons — stack vertically on phones */}
      <DialogActions
        sx={{ pt: 2, flexDirection: isMobile ? "column" : "row" }}
      >
        <Button
          onClick={onClose}
          variant="text"
          fullWidth={isMobile}
          sx={{ textTransform: "none", mb: isMobile ? 1 : 0 }}
        >
          Cancel
        </Button>

        <Button
          startIcon={<NativeShareIcon size={18} />}
          onClick={handleNativeShare}
          variant="contained"
          fullWidth={isMobile}
          sx={{
            textTransform: "none",
            px: isMobile ? 2 : 3,
            borderRadius: 2,
            background: "linear-gradient(90deg,#6A4DF4 0%,#9D67FF 100%)",
            ":hover": {
              background: "linear-gradient(90deg,#5E44E4 0%,#9058F4 100%)",
            },
          }}
        >
          Share
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareProfileModal;
